import { IconButton } from '@chakra-ui/button';
import Icon from '@chakra-ui/icon';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Flex,
  FormControl,
  FormLabel,
  Stack,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React from 'react';
import { useForm } from 'react-hook-form';
import { FaRegTimesCircle } from 'react-icons/fa';
import { Button, ColorfulBar, Input } from '~/components';
import { saveAdditionalDiscount } from '~/services/Discounts';
import { AdditionalDiscount } from '~/types/discount';
import { formatCurrency } from '~/utils/formatCurrency';
import { queryClient } from '~/utils/react-query';

interface EditableRowAdditionalDiscountProps {
  item: AdditionalDiscount;
  handleDelete: (id: number | undefined) => void;
}

export const EditableRowAdditionalDiscount: React.FC<EditableRowAdditionalDiscountProps> =
  ({ item, handleDelete }: EditableRowAdditionalDiscountProps) => {
    const toast = useToast();
    const [form, setForm] = React.useState<AdditionalDiscount>({
      de: item.de,
      ate: item.ate,
      desconto: item.desconto,
    });

    const { isOpen, onOpen, onClose } = useDisclosure();

    const {
      register,
      handleSubmit,
      setValue,
      reset,
      setError,
      formState,
      clearErrors,
      control,
    } = useForm();
    const { errors } = formState;

    const parse = (valueAsString: string) => {
      let valueAsNumber = 0;
      try {
        let parsedValueAsString = valueAsString.replace(/\D/g, '');
        parsedValueAsString = parsedValueAsString.padStart(2 + 1, '0');
        parsedValueAsString = `${parsedValueAsString.substring(
          0,
          parsedValueAsString.length - 2,
        )}.${parsedValueAsString.slice(2 * -1)}`;
        valueAsNumber = parseFloat(parsedValueAsString);
      } catch {
        valueAsNumber = 0;
      }
      return valueAsNumber;
    };

    const [descontoFormatado, setDescontoFormatado] = React.useState(
      formatCurrency(parse(String(item.desconto))).replace('R$', ''),
    );

    const isFormValid = () => {
      clearErrors();
      !form.de &&
        setError('de', {
          type: 'required',
          message: 'Campo obrigatório',
        });

      !form.ate &&
        setError('ate', {
          type: 'required',
          message: 'Campo obrigatório',
        });
      !form.desconto &&
        setError('desconto', {
          type: 'required',
          message: 'Campo obrigatório',
        });

      Number(form.de) > 2147483647 &&
        setError('de', {
          type: 'max',
          message: 'Valor inválido',
        });

      Number(form.ate) > 2147483647 &&
        setError('ate', {
          type: 'max',
          message: 'Valor inválido',
        });

      Number(form.ate) >= Number(form.de) &&
        setError('ate', {
          type: 'custom',
          message: "Valor deve ser inferior ao 'de'",
        });

      !(form.desconto.toString().length <= 6) &&
        setError('desconto', {
          type: 'custom',
          message: 'Valor inválido',
        });

      const { _formState } = control;
      return _formState.isValid;
    };

    const salvar = () => {
      if (!isFormValid()) {
        return;
      }

      if (item?.id) {
        if (
          form.de !== item.de ||
          form.ate !== item.ate ||
          Number(form.desconto) !== Number(item.desconto)
        ) {
          saveAdditionalDiscount(item.id, form)
            .then(() => {
              queryClient.invalidateQueries(['additional-discounts']);
              toast({
                title: 'Desconto alterado',
                status: 'success',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
              });
            })
            .catch(err => {
              toast({
                title: `${err.response.data.message}`,
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
              });
            });
        }
      } else if (
        (form.de !== item.de || form.de <= item.de) &&
        form.ate !== item.ate &&
        form.desconto !== item.desconto
      ) {
        saveAdditionalDiscount(null, form)
          .then(() => {
            queryClient.invalidateQueries(['additional-discounts']);
            toast({
              title: 'Novo desconto criado',
              status: 'success',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          })
          .catch(err => {
            toast({
              title: `${err.response.data.message}`,
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          });
      }
    };

    const handleDe = (e: React.FormEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value.replace(/\D/g, '');
      setForm({
        ...form,
        de: Number(value),
      });
      clearErrors('de');
    };

    const handleAte = (e: React.FormEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value.replace(/\D/g, '');
      setForm({
        ...form,
        ate: Number(value),
      });
      clearErrors('ate');
    };

    const handleDesconto = (e: React.FormEvent<HTMLInputElement>) => {
      const value = parse(e.currentTarget.value);
      setForm({
        ...form,
        desconto: value,
      });
      clearErrors('desconto');
      setDescontoFormatado(formatCurrency(value).replace('R$', ''));
    };

    return (
      <Stack p="2" direction="row">
        <FormControl isInvalid={errors.de} w="70%">
          <FormLabel
            htmlFor="writeUpFile"
            style={{ fontSize: '14px' }}
            justifyContent="space-between"
            display="flex"
          >
            De *
          </FormLabel>
          <Input
            name="de"
            resize="none"
            error={errors?.de}
            value={form.de}
            onBlur={salvar}
            onChange={e => handleDe(e)}
            fontSize="15px"
            width="100%"
            pattern="[0-9]*"
          />
        </FormControl>
        <FormControl isInvalid={errors.ate} w="70%">
          <FormLabel
            htmlFor="writeUpFile"
            style={{ fontSize: '14px' }}
            justifyContent="space-between"
            display="flex"
          >
            Até *
          </FormLabel>
          <Input
            name="ate"
            resize="none"
            error={errors?.ate}
            value={form.ate}
            onBlur={salvar}
            onChange={e => handleAte(e)}
            fontSize="15px"
            width="100%"
            pattern="[0-9]*"
          />
        </FormControl>
        <FormControl isInvalid={errors.desconto}>
          <FormLabel
            htmlFor="writeUpFile"
            style={{ fontSize: '14px' }}
            justifyContent="space-between"
            display="flex"
          >
            % Desconto adicional *
          </FormLabel>
          <Input
            name="desconto"
            resize="none"
            error={errors?.desconto}
            value={descontoFormatado}
            onChange={e => handleDesconto(e)}
            onBlur={salvar}
            fontSize="15px"
            width="100%"
          />
        </FormControl>
        <Flex h="auto" alignItems={formState.isValid ? 'end' : 'center'}>
          <Tooltip
            label="Excluir desconto"
            marginBottom={formState.isValid ? 2 : ''}
          >
            <IconButton
              colorScheme="gray"
              background="transparent"
              _hover={{
                background: 'transparent',
              }}
              aria-label="delete"
              icon={<Icon as={FaRegTimesCircle} w={6} h={6} />}
              onClick={onOpen}
            />
          </Tooltip>
        </Flex>
        <AlertDialog
          motionPreset="slideInBottom"
          leastDestructiveRef={undefined}
          onClose={onClose}
          isOpen={isOpen}
          isCentered
          closeOnOverlayClick={false}
          size="xl"
        >
          <AlertDialogOverlay />

          <AlertDialogContent width={800}>
            <ColorfulBar />
            <AlertDialogHeader>Atenção</AlertDialogHeader>
            <AlertDialogBody>Deseja excluir este desconto?</AlertDialogBody>
            <AlertDialogFooter>
              <Button
                onClick={() => {
                  onClose();
                }}
                variant="secondary"
                width={100}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  handleDelete(item?.id);
                  onClose();
                }}
                variant="primary"
                width={100}
                ml={3}
              >
                OK
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Stack>
    );
  };
